import classNames from 'classnames';
import JSCookie from 'js-cookie';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useOrganizationDetails, useOrganizationMenuByAlias } from 'src/store/hooks/organizations';
import { useUser, useUserInfo } from 'src/store/hooks/users';
import { TCreateReviewPageQuery } from 'src/types/router';

import { Menu, Space, Divider, Typography, Icon } from '@sravni/react-design-system';
import { ArrowLeft, ArrowRight } from '@sravni/react-icons';
import { checkIfMfo } from '@sravni/ugc/lib/utils/checkOrganizationsType';

import styles from './organization-menu.module.scss';

interface IProps {
  organizationAlias: string;
  currentAlias: string;
  isMobile: boolean;
}

export const OrganizationMenu: React.FC<IProps> = ({ organizationAlias, currentAlias, isMobile }) => {
  const { query } = useRouter();
  const { organization, type } = (query as unknown) as TCreateReviewPageQuery;
  const userInfo = useUserInfo();
  const user = useUser();
  const menu = useOrganizationMenuByAlias(organizationAlias);
  const { _id, id } = useOrganizationDetails(organization);

  const tabsRef = useRef<HTMLDivElement>(null);
  const menuItemRef = useRef<HTMLAnchorElement>(null);
  const menuItemActiveRef = useRef<HTMLAnchorElement>(null);

  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const updateArrows = useCallback(() => {
    if (tabsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabsRef.current;
      const roundScrollLeftValue = Math.round(scrollLeft);
      const isOveflow = scrollWidth > clientWidth;
      setIsOverflowing(isOveflow);

      setShowLeftArrow(isOveflow && roundScrollLeftValue > 0);
      setShowRightArrow(isOveflow && roundScrollLeftValue + 1 < scrollWidth - clientWidth);
    }
  }, []);

  const scroll = useCallback((direction) => {
    if (tabsRef.current) {
      const { clientWidth } = tabsRef.current;
      const scrollAmount = clientWidth / 2;
      tabsRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  }, []);

  const getShowPur = useCallback(() => {
    const showCookie = JSCookie.get('SHOW_ME_PUR');
    const organizationId = _id || id;

    const isRepresentator = organizationId.toString() === userInfo?.companyId?.toString();
    const isModerator =
      user?.role?.includes('Sravni.Reviews.Service-Moderator') || user?.role?.includes('Sravni.Reviews.Service-Admin');

    return showCookie || isRepresentator || isModerator;
  }, [_id, id, user?.role, userInfo?.companyId]);

  const getLinkPUR = useCallback(
    () => (checkIfMfo(type) ? `/${type}/mfo/${organization}/reputation/` : `/${type}/${organization}/reputation/`),
    [organization, type],
  );

  const isShowPUR = getShowPur() && !menu.some((el) => el.label === 'Управление репутацией');

  const configuredMenu = useCallback(() => {
    const configuredMenuArr = menu ? [...menu] : [];

    if (isShowPUR) {
      configuredMenuArr.push({
        href: getLinkPUR(),
        isLink: true,
        label: 'Управление репутацией',
      });
    }

    return configuredMenuArr;
  }, [getLinkPUR, isShowPUR, menu]);

  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [menu, updateArrows]);

  useEffect(() => {
    if (tabsRef.current && menuItemActiveRef.current) {
      tabsRef.current.scrollLeft = menuItemActiveRef.current.offsetLeft - 20;
    }
  }, []);

  const getConfiguredMenu = useMemo(() => {
    if (!menu?.length) return null;

    return (
      <>
        <div className={classNames(styles.horizontalMenuContainer, 'h-mt-16')}>
          <Space size={[20, 0]} wrap={false} ref={tabsRef} className={styles.horizontalMenu} onScroll={updateArrows}>
            {showLeftArrow && isOverflowing && <div className={classNames(styles.gradient, styles.leftGradient)} />}
            {showLeftArrow && !isMobile && (
              <Icon
                className={classNames(styles.arrowButton, styles.leftArrow)}
                onClick={() => scroll('left')}
                icon={<ArrowLeft color="var(--color-D60)" />}
              />
            )}
            {configuredMenu().map(({ href, isLink, label, children }) => (
              <Typography.Link
                className={classNames(styles.tab, {
                  [styles.active]: href?.includes(currentAlias),
                  [styles.link]: isLink,
                })}
                href={href}
                rel={'noreferrer'}
                ref={href?.includes(currentAlias) ? menuItemActiveRef : menuItemRef}
              >
                {label}
                {children?.length && (
                  <div className={styles.subMenu}>
                    <Menu>
                      {children.map(({ label: childrenLabel, href: childrenHref }, key) => (
                        <Menu.Item key={key}>
                          <Typography.Link
                            href={childrenHref}
                            target={'_blank'}
                            className={styles.subLink}
                            rel="noreferrer"
                          >
                            {childrenLabel}
                          </Typography.Link>
                        </Menu.Item>
                      ))}
                    </Menu>
                  </div>
                )}
              </Typography.Link>
            ))}
            {showRightArrow && !isMobile && (
              <Icon
                className={classNames(styles.arrowButton, styles.rightArrow)}
                onClick={() => scroll('right')}
                icon={<ArrowRight color="var(--color-D60)" />}
              />
            )}
            {showRightArrow && isOverflowing && <div className={classNames(styles.gradient, styles.rightGradient)} />}
          </Space>
        </div>
        <Divider className={styles.divider} />
      </>
    );
  }, [
    configuredMenu,
    currentAlias,
    isMobile,
    isOverflowing,
    menu?.length,
    scroll,
    showLeftArrow,
    showRightArrow,
    updateArrows,
  ]);

  return getConfiguredMenu;
};
